import { useState, useMemo } from 'react';
import {
  Box, Paper, Typography, Chip, Drawer, IconButton, Collapse,
  TextField, FormControl, InputLabel, Select, MenuItem, Button, Divider, Link,
} from '@mui/material';
import CloseIcon       from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import OpenInNewIcon   from '@mui/icons-material/OpenInNew';
import LockIcon        from '@mui/icons-material/Lock';

// ── Status enum (with audit-friendly colors) ────────────────────
const STATUS = [
  { val: 'compliant',     label: 'Compliant',     color: '#4caf50' },
  { val: 'partial',       label: 'Partial',       color: '#ffeb3b' },
  { val: 'non_compliant', label: 'Non-Compliant', color: '#f44336' },
  { val: 'na',            label: 'N/A',           color: '#7a8fa6' },
  { val: 'not_assessed',  label: 'Not Assessed',  color: '#4a5a70' },
];
const STATUS_LABEL = Object.fromEntries(STATUS.map(s => [s.val, s.label]));
const STATUS_COLOR = Object.fromEntries(STATUS.map(s => [s.val, s.color]));

// ── Categories ──────────────────────────────────────────────────
const CATEGORIES = [
  'Insider Threat',
  'SIEM / Continuous Monitoring',
  'Audit & Accountability',
  'COOP / Disaster Recovery',
  'Access Control',
  'Configuration Management',
  'Incident Response',
  'Risk Management',
  'System & Information Integrity',
  'Identification & Authentication',
];

// ── Seed requirements (CORA-oriented) ───────────────────────────
// id, category, reference, title, requirement, status, evidenceUrl, notes,
// reviewedAt (ISO date string), reviewedBy
const SEED = [
  // Insider Threat
  { id: 1, category: 'Insider Threat', reference: 'DODD 5205.16',  title: 'Insider Threat Program',
    requirement: 'Establish and maintain a DoD Insider Threat Program to detect, deter, and mitigate insider threats per DODD 5205.16 and NITTF Minimum Standards.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 2, category: 'Insider Threat', reference: 'DODI 5240.26', title: 'Counterintelligence Awareness & Reporting',
    requirement: 'Provide annual CI awareness training and require reporting of suspicious contacts, behaviors, and cyber incidents.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // SIEM / ConMon
  { id: 3, category: 'SIEM / Continuous Monitoring', reference: 'DODI 8530.01', title: 'Cybersecurity Activities Support',
    requirement: 'Implement continuous monitoring sensors and forward security-relevant events to a centralized SIEM with defined retention.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 4, category: 'SIEM / Continuous Monitoring', reference: 'NIST 800-53 SI-4', title: 'System Monitoring',
    requirement: 'Monitor the system to detect attacks and indicators of potential attacks; identify unauthorized use of the system.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 5, category: 'SIEM / Continuous Monitoring', reference: 'NIST 800-53 CA-7', title: 'Continuous Monitoring Strategy',
    requirement: 'Maintain a documented continuous monitoring strategy and implement a continuous monitoring program.',
    status: 'not_assessed', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // Audit & Accountability
  { id: 6, category: 'Audit & Accountability', reference: 'NIST 800-53 AU-2', title: 'Event Logging',
    requirement: 'Identify the types of events the system is capable of logging; coordinate the event logging function with other org entities.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 7, category: 'Audit & Accountability', reference: 'NIST 800-53 AU-3', title: 'Content of Audit Records',
    requirement: 'Audit records contain information establishing what type of event occurred, when, where, source, outcome, and identity of users/subjects associated with the event.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 8, category: 'Audit & Accountability', reference: 'NIST 800-53 AU-6', title: 'Audit Record Review, Analysis & Reporting',
    requirement: 'Review and analyze audit records for indications of inappropriate or unusual activity; report findings to designated personnel.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 9, category: 'Audit & Accountability', reference: 'NIST 800-53 AU-9', title: 'Protection of Audit Information',
    requirement: 'Protect audit information and audit logging tools from unauthorized access, modification, and deletion.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 10, category: 'Audit & Accountability', reference: 'NIST 800-53 AU-12', title: 'Audit Record Generation',
    requirement: 'Provide audit record generation capability for the event types defined in AU-2 on all system components.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // COOP / DR
  { id: 11, category: 'COOP / Disaster Recovery', reference: 'DODI 3020.42', title: 'Defense Continuity Program',
    requirement: 'Maintain a Continuity of Operations Plan (COOP) ensuring essential functions can be performed during a wide range of emergencies.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 12, category: 'COOP / Disaster Recovery', reference: 'NIST 800-53 CP-2', title: 'Contingency Plan',
    requirement: 'Develop a contingency plan that identifies essential mission/business functions, recovery objectives, and roles/responsibilities.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 13, category: 'COOP / Disaster Recovery', reference: 'NIST 800-53 CP-7', title: 'Alternate Processing Site',
    requirement: 'Establish an alternate processing site that permits the transfer and resumption of essential mission/business functions within RTO.',
    status: 'not_assessed', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 14, category: 'COOP / Disaster Recovery', reference: 'NIST 800-53 CP-9', title: 'System Backup',
    requirement: 'Conduct backups of user-level, system-level, and system documentation information at organization-defined frequency.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 15, category: 'COOP / Disaster Recovery', reference: 'NIST 800-53 CP-10', title: 'System Recovery & Reconstitution',
    requirement: 'Provide for the recovery and reconstitution of the system to a known state after a disruption, compromise, or failure.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // Access Control
  { id: 16, category: 'Access Control', reference: 'NIST 800-53 AC-2', title: 'Account Management',
    requirement: 'Manage system accounts including establishment, activation, modification, review, disabling, and removal.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 17, category: 'Access Control', reference: 'NIST 800-53 AC-3', title: 'Access Enforcement',
    requirement: 'Enforce approved authorizations for logical access to information and system resources per access control policy.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 18, category: 'Access Control', reference: 'NIST 800-53 AC-6', title: 'Least Privilege',
    requirement: 'Employ the principle of least privilege; allow only authorized accesses necessary to accomplish assigned tasks.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // Configuration Management
  { id: 19, category: 'Configuration Management', reference: 'NIST 800-53 CM-2', title: 'Baseline Configuration',
    requirement: 'Develop, document, and maintain a current baseline configuration of the system under configuration control.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 20, category: 'Configuration Management', reference: 'NIST 800-53 CM-6', title: 'Configuration Settings',
    requirement: 'Establish and document configuration settings that reflect the most restrictive mode consistent with operational requirements (STIGs).',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 21, category: 'Configuration Management', reference: 'NIST 800-53 CM-7', title: 'Least Functionality',
    requirement: 'Configure the system to provide only essential capabilities; prohibit or restrict use of non-essential functions, ports, protocols, and services.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // Incident Response
  { id: 22, category: 'Incident Response', reference: 'NIST 800-53 IR-4', title: 'Incident Handling',
    requirement: 'Implement an incident handling capability for incidents that includes preparation, detection and analysis, containment, eradication, and recovery.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 23, category: 'Incident Response', reference: 'NIST 800-53 IR-6', title: 'Incident Reporting',
    requirement: 'Require personnel to report suspected security incidents to the organizational incident response capability within designated time periods.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 24, category: 'Incident Response', reference: 'NIST 800-53 IR-8', title: 'Incident Response Plan',
    requirement: 'Develop an incident response plan that provides the organization with a roadmap for implementing IR capability.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // Risk Management
  { id: 25, category: 'Risk Management', reference: 'NIST 800-53 RA-3', title: 'Risk Assessment',
    requirement: 'Conduct risk assessments including likelihood and impact of harm; document results and disseminate to defined personnel.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 26, category: 'Risk Management', reference: 'NIST 800-53 RA-5', title: 'Vulnerability Monitoring & Scanning',
    requirement: 'Monitor and scan for vulnerabilities in the system and hosted applications per defined frequency (ACAS).',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // System & Information Integrity
  { id: 27, category: 'System & Information Integrity', reference: 'NIST 800-53 SI-2', title: 'Flaw Remediation',
    requirement: 'Identify, report, and correct system flaws; test software and firmware updates before installation.',
    status: 'partial', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 28, category: 'System & Information Integrity', reference: 'NIST 800-53 SI-7', title: 'Software, Firmware & Information Integrity',
    requirement: 'Employ integrity verification tools to detect unauthorized changes to organization-defined software, firmware, and information.',
    status: 'not_assessed', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },

  // I&A
  { id: 29, category: 'Identification & Authentication', reference: 'NIST 800-53 IA-2', title: 'Identification & Authentication (Org Users)',
    requirement: 'Uniquely identify and authenticate organizational users and associate identifications with processes acting on behalf of those users (CAC/PIV).',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
  { id: 30, category: 'Identification & Authentication', reference: 'NIST 800-53 IA-5', title: 'Authenticator Management',
    requirement: 'Manage system authenticators including initial distribution, lost/compromised handling, and changing/refreshing.',
    status: 'compliant', evidenceUrl: '', notes: '', reviewedAt: '', reviewedBy: '' },
];

// ── Status chip ────────────────────────────────────────────────
function StatusChip({ value }) {
  const color = STATUS_COLOR[value] || '#7a8fa6';
  return (
    <Chip
      label={STATUS_LABEL[value] || value}
      size="small"
      sx={{
        height: 20,
        fontSize: 10,
        fontWeight: 700,
        color,
        backgroundColor: `${color}22`,
        border: `1px solid ${color}55`,
      }}
    />
  );
}

// ── Requirement row ────────────────────────────────────────────
function RequirementRow({ item, onClick }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr 140px 28px',
        gap: 1.5,
        alignItems: 'center',
        padding: '10px 14px',
        borderBottom: '1px solid #1a2840',
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#0d1f33' },
      }}
    >
      <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 11, fontWeight: 700 }}>
        {item.reference}
      </Typography>
      <Box>
        <Typography sx={{ color: '#c0cfe0', fontSize: 13, fontWeight: 600 }}>{item.title}</Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.requirement}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <StatusChip value={item.status} />
      </Box>
      <Typography sx={{ color: item.evidenceUrl ? '#4caf50' : '#2a3a50', fontSize: 16, textAlign: 'center' }} title={item.evidenceUrl ? 'Evidence linked' : 'No evidence'}>
        {item.evidenceUrl ? '●' : '○'}
      </Typography>
    </Box>
  );
}

// ── Category section ───────────────────────────────────────────
function CategorySection({ category, items, onSelect }) {
  const [open, setOpen] = useState(true);
  const counts = useMemo(() => {
    const c = { compliant: 0, partial: 0, non_compliant: 0, na: 0, not_assessed: 0 };
    items.forEach(i => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);

  return (
    <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, mb: 2, overflow: 'hidden' }}>
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', borderBottom: open ? '1px solid #1e3a5f' : 'none',
          cursor: 'pointer', backgroundColor: '#0d1825',
          '&:hover': { backgroundColor: '#0d2137' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {open ? <KeyboardArrowDownIcon sx={{ color: '#00bcd4', fontSize: 18 }} /> : <KeyboardArrowRightIcon sx={{ color: '#7a8fa6', fontSize: 18 }} />}
          <Typography sx={{ color: '#ffffff', fontWeight: 700, fontSize: 14 }}>{category}</Typography>
          <Chip label={`${items.length}`} size="small" sx={{ height: 18, fontSize: 10, backgroundColor: '#1e3a5f', color: '#7a8fa6' }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {STATUS.map(s => counts[s.val] > 0 && (
            <Chip
              key={s.val}
              label={`${counts[s.val]} ${s.label}`}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 700, color: s.color, backgroundColor: `${s.color}22`, border: `1px solid ${s.color}44` }}
            />
          ))}
        </Box>
      </Box>
      <Collapse in={open}>
        {items.map(it => <RequirementRow key={it.id} item={it} onClick={() => onSelect(it)} />)}
      </Collapse>
    </Paper>
  );
}

// ── Detail drawer ──────────────────────────────────────────────
function DetailDrawer({ item, onClose, onSave, canEdit }) {
  const [form, setForm] = useState(item);
  if (!item) return null;
  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const FIELD_SX = {
    '& .MuiOutlinedInput-root': {
      color: '#c0cfe0', fontSize: 13,
      '& fieldset': { borderColor: '#1e3a5f' },
      '&:hover fieldset': { borderColor: '#00bcd4' },
      '&.Mui-focused fieldset': { borderColor: '#00bcd4' },
    },
    '& .MuiInputLabel-root': { color: '#7a8fa6', fontSize: 13 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#00bcd4' },
    '& .MuiSelect-icon': { color: '#7a8fa6' },
  };

  const handleSave = () => {
    onSave({
      ...form,
      reviewedAt: new Date().toISOString().slice(0, 10),
    });
    onClose();
  };

  return (
    <Drawer anchor="right" open={Boolean(item)} onClose={onClose}
      PaperProps={{ sx: { width: 480, backgroundColor: '#0d1825', borderLeft: '1px solid #1e3a5f' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #1e3a5f' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: '#00bcd4', fontFamily: 'monospace', fontSize: 12, fontWeight: 700 }}>
            {item.reference}
          </Typography>
          <Typography sx={{ color: '#ffffff', fontSize: 15, fontWeight: 700, mt: 0.25 }}>{item.title}</Typography>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, mt: 0.25 }}>{item.category}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: '#7a8fa6' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 2.5, overflowY: 'auto' }}>
        <Box>
          <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Requirement</Typography>
          <Typography sx={{ color: '#c0cfe0', fontSize: 13, lineHeight: 1.6 }}>{item.requirement}</Typography>
        </Box>

        <Divider sx={{ borderColor: '#1e3a5f' }} />

        {!canEdit && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '8px 10px', backgroundColor: '#0a1525', borderRadius: 1, border: '1px solid #1e3a5f' }}>
            <LockIcon sx={{ fontSize: 14, color: '#7a8fa6' }} />
            <Typography sx={{ color: '#7a8fa6', fontSize: 11 }}>Read-only view — auditor mode</Typography>
          </Box>
        )}

        <FormControl size="small" fullWidth sx={FIELD_SX} disabled={!canEdit}>
          <InputLabel>Compliance Status</InputLabel>
          <Select value={form.status} onChange={set('status')} label="Compliance Status"
            MenuProps={{ PaperProps: { sx: { backgroundColor: '#111827', border: '1px solid #1e3a5f' } } }}>
            {STATUS.map(s => (
              <MenuItem key={s.val} value={s.val} sx={{ color: s.color, fontSize: 13 }}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Evidence URL"
          value={form.evidenceUrl}
          onChange={set('evidenceUrl')}
          size="small"
          fullWidth
          disabled={!canEdit}
          placeholder="https://policy.aspyn.mil/insider-threat.pdf"
          sx={FIELD_SX}
        />
        {form.evidenceUrl && (
          <Link href={form.evidenceUrl} target="_blank" rel="noopener" sx={{ color: '#00bcd4', fontSize: 12, display: 'flex', alignItems: 'center', gap: 0.5, mt: -1.5 }}>
            <OpenInNewIcon sx={{ fontSize: 13 }} /> Open evidence
          </Link>
        )}

        <TextField
          label="Notes / Auditor Response"
          value={form.notes}
          onChange={set('notes')}
          size="small"
          fullWidth
          multiline
          rows={5}
          disabled={!canEdit}
          placeholder="How this requirement is satisfied, who is responsible, any gaps or POA&M items..."
          sx={FIELD_SX}
        />

        {(item.reviewedAt || item.reviewedBy) && (
          <Box sx={{ padding: '8px 10px', backgroundColor: '#0a1525', borderRadius: 1, border: '1px solid #1e3a5f' }}>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Last Reviewed</Typography>
            <Typography sx={{ color: '#c0cfe0', fontSize: 12, mt: 0.25 }}>
              {item.reviewedAt || '—'}{item.reviewedBy ? ` · ${item.reviewedBy}` : ''}
            </Typography>
          </Box>
        )}

        {canEdit && (
          <>
            <Divider sx={{ borderColor: '#1e3a5f' }} />
            <Button
              variant="contained"
              onClick={handleSave}
              fullWidth
              sx={{ backgroundColor: '#00bcd4', color: '#000', fontWeight: 700, fontSize: 13, '&:hover': { backgroundColor: '#00acc1' } }}
            >
              Save
            </Button>
          </>
        )}
      </Box>
    </Drawer>
  );
}

// ── Main page ──────────────────────────────────────────────────
export default function Odp({ currentUser }) {
  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'issm';
  const [items, setItems] = useState(SEED);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus]     = useState('all');
  const [selected, setSelected]             = useState(null);

  const totals = useMemo(() => {
    const t = { total: items.length, compliant: 0, partial: 0, non_compliant: 0, na: 0, not_assessed: 0 };
    items.forEach(i => { t[i.status] = (t[i.status] || 0) + 1; });
    return t;
  }, [items]);

  const filtered = useMemo(() => items.filter(i =>
    (filterCategory === 'all' || i.category === filterCategory) &&
    (filterStatus   === 'all' || i.status   === filterStatus)
  ), [items, filterCategory, filterStatus]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(i => {
      if (!map[i.category]) map[i.category] = [];
      map[i.category].push(i);
    });
    return CATEGORIES.filter(c => map[c]).map(c => [c, map[c]]);
  }, [filtered]);

  const handleSave = (updated) => {
    setItems(prev => prev.map(i => i.id === updated.id
      ? { ...updated, reviewedBy: currentUser?.username || 'unknown' }
      : i
    ));
  };

  return (
    <Box sx={{ backgroundColor: '#0a0e1a', minHeight: '100vh', padding: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ color: '#ffffff', fontWeight: 700 }}>
          Orders, Directives & Policies
        </Typography>
        <Typography sx={{ color: '#7a8fa6', fontSize: 13, mt: 0.5 }}>
          CORA-aligned compliance register — track applicable DoD orders, NIST 800-53 controls, and supporting policy evidence per requirement.
          {!canEdit && ' Read-only mode (auditor).'}
        </Typography>
      </Box>

      {/* Stat cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 2, mb: 3 }}>
        {[
          { label: 'Total',         val: totals.total,         color: '#c0cfe0' },
          { label: 'Compliant',     val: totals.compliant,     color: '#4caf50' },
          { label: 'Partial',       val: totals.partial,       color: '#ffeb3b' },
          { label: 'Non-Compliant', val: totals.non_compliant, color: '#f44336' },
          { label: 'N/A',           val: totals.na,            color: '#7a8fa6' },
          { label: 'Not Assessed',  val: totals.not_assessed,  color: '#4a5a70' },
        ].map(s => (
          <Paper key={s.label} sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, padding: 2, textAlign: 'center' }}>
            <Typography sx={{ color: s.color, fontSize: 28, fontWeight: 700, lineHeight: 1.1 }}>{s.val}</Typography>
            <Typography sx={{ color: '#7a8fa6', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.5 }}>{s.label}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Category:</Typography>
          {['all', ...CATEGORIES].map(c => {
            const active = filterCategory === c;
            return (
              <Chip
                key={c}
                label={c === 'all' ? 'All' : c}
                size="small"
                onClick={() => setFilterCategory(c)}
                sx={{
                  fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer',
                  color: active ? '#00bcd4' : '#7a8fa6',
                  backgroundColor: active ? '#00bcd422' : '#111827',
                  border: `1px solid ${active ? '#00bcd4' : '#1e3a5f'}`,
                  '&:hover': { color: '#00bcd4', backgroundColor: '#00bcd411' },
                }}
              />
            );
          })}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ color: '#7a8fa6', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Status:</Typography>
        {[{ val: 'all', label: 'All', color: '#c0cfe0' }, ...STATUS].map(s => {
          const active = filterStatus === s.val;
          return (
            <Chip
              key={s.val}
              label={s.label}
              size="small"
              onClick={() => setFilterStatus(s.val)}
              sx={{
                fontSize: 11, fontWeight: active ? 700 : 400, cursor: 'pointer',
                color: active ? s.color : '#7a8fa6',
                backgroundColor: active ? `${s.color}22` : '#111827',
                border: `1px solid ${active ? s.color : '#1e3a5f'}`,
                '&:hover': { color: s.color, backgroundColor: `${s.color}11` },
              }}
            />
          );
        })}
      </Box>

      {/* Column headers */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr 140px 28px', gap: 1.5, padding: '0 14px 6px', borderBottom: '1px solid #1e3a5f' }}>
        {['Reference', 'Requirement', 'Status', 'Ev'].map(h => (
          <Typography key={h} sx={{ color: '#7a8fa6', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</Typography>
        ))}
      </Box>

      {/* Category sections */}
      <Box sx={{ mt: 2 }}>
        {grouped.length === 0 ? (
          <Typography sx={{ color: '#4a5a70', textAlign: 'center', padding: 6, fontStyle: 'italic' }}>
            No requirements match the current filters.
          </Typography>
        ) : (
          grouped.map(([cat, list]) => (
            <CategorySection key={cat} category={cat} items={list} onSelect={setSelected} />
          ))
        )}
      </Box>

      <DetailDrawer item={selected} onClose={() => setSelected(null)} onSave={handleSave} canEdit={canEdit} />
    </Box>
  );
}
